/**
 * Width-aware calendar layout. Pure so it is testable in node.
 *
 * A horizontal calendar gives each week one column, so a one-year range
 * at 320px leaves ~5px-wide cells and colliding month labels. Narrow
 * screens switch to a vertical calendar (weeks run down the page, days
 * across), which grows taller instead. Print always stays horizontal —
 * a decided trade-off: a PDF's pagination shouldn't change with column
 * width — and only abbreviates its labels.
 */

export const CAL_MIN_CELL_PX = 9;
const CAL_V_CELL = { min: 10, max: 16 } as const;
const CAL_V_MAX_HEIGHT = 900;
const MARGIN = { left: 36, right: 16, top: 32, bottom: 56 } as const;

const DAY_INITIALS = ["S", "M", "T", "W", "T", "F", "S"];

export interface CalendarLayout {
  orient: "horizontal" | "vertical";
  cellSize: [number | "auto", number | "auto"];
  left: number;
  right: number;
  top: number;
  bottom: number;
  /** Height the chart needs, px; undefined = use the normal default. */
  height?: number;
  monthLabel: Record<string, unknown>;
  dayLabel: Record<string, unknown>;
  /** visualMap ramp length, px. */
  rampLength: number;
}

const weeksIn = ([start, end]: readonly [string, string]): number => {
  const days = (Date.parse(end) - Date.parse(start)) / 86_400_000 + 1;
  return Math.ceil((Number.isFinite(days) && days > 0 ? days : 365) / 7) + 1;
};

export function calendarLayout(input: {
  range: readonly [string, string];
  width: number;
  print: boolean;
  specHeight?: number;
}): CalendarLayout {
  const { width, print } = input;
  const weeks = weeksIn(input.range);
  const rampLength = width > 0 ? Math.min(120, Math.max(60, width - 140)) : 120;
  const horizontalCell = width > 0 ? (width - MARGIN.left - MARGIN.right) / weeks : Infinity;
  const base = {
    ...MARGIN,
    rampLength,
  };
  if (width <= 0 || print || horizontalCell >= CAL_MIN_CELL_PX) {
    // A month spans ~4.3 week columns; below ~24px the 3-letter name
    // collides with its neighbour, so drop to the initial.
    const tightMonths = width > 0 && horizontalCell * 4.3 < 24;
    return {
      orient: "horizontal",
      cellSize: ["auto", "auto"],
      ...base,
      monthLabel: tightMonths
        ? { formatter: (p: { nameMap?: string }) => (p.nameMap ?? "").charAt(0) }
        : {},
      dayLabel: {},
    };
  }
  // The cell size is fixed, so the height has to follow it: capping the
  // height alone would draw the last weeks over the visualMap ramp. Shrink
  // the cell to fit the cap first; a range too long even at the minimum
  // cell (~2 years) grows past the cap rather than clipping.
  const margins = MARGIN.top + MARGIN.bottom;
  const cell = Math.max(
    CAL_V_CELL.min,
    Math.min(
      CAL_V_CELL.max,
      Math.floor((width - 48) / 7),
      Math.floor((CAL_V_MAX_HEIGHT - margins) / weeks),
    ),
  );
  const derived = weeks * cell + margins;
  return {
    orient: "vertical",
    cellSize: [cell, cell],
    ...base,
    height: Math.max(derived, Math.min(CAL_V_MAX_HEIGHT, input.specHeight ?? 0)),
    monthLabel: {},
    dayLabel: { nameMap: DAY_INITIALS },
  };
}
